// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
//
// A comment she pins on the artwork: saves it, then opens the Comments pane
// and selects it once the review carries it. With Send to Puntt on, the same
// text also goes to the chat with the new comment's pin attached.
import { useCallback, useEffect, useMemo, useState } from "react";

import { useCreateAskPunttReviewComment } from "./useAskPunttReview";
import { type AskV2ReviewLeftPane } from "./useAskV2ReviewWorkspace";

import { type HumanCommentSubmitInput } from "../../components/ask-puntt-review";
import { type ComposeSendToPuntt } from "../../components/ask-puntt-review/composeSendToPuntt";
import { useIsFlagOn } from "../../config/featureFlags";
import { type AskV2CommentContext } from "../transport/types";
import { type AskV2TransportApi } from "../transport/useAskV2Transport";

type HumanCommentInput = {
  reviewId: string | null | undefined;
  comments: readonly { _id: string }[] | undefined;
  setLeftPane: (pane: AskV2ReviewLeftPane) => void;
  selectComment: (commentId: string) => void;
  commentContextFor: (commentId: string) => AskV2CommentContext | null;
  transport: Pick<
    AskV2TransportApi,
    "setCommentContext" | "removeCommentContext" | "send" | "sendWhenIdle"
  >;
};

type PendingComment = { id: string; askPuntt: string | null };

export function useAskV2ReviewHumanComment({
  reviewId,
  comments,
  setLeftPane,
  selectComment,
  commentContextFor,
  transport,
}: HumanCommentInput) {
  const { mutateAsync: createReviewComment } =
    useCreateAskPunttReviewComment(reviewId);
  const offerSend = useIsFlagOn("ask-v2-review-send-to-puntt");
  const [sendChecked, setSendChecked] = useState(false);
  const [pending, setPending] = useState<PendingComment | null>(null);
  const handleSubmitHumanComment = useCallback(
    async (input: HumanCommentSubmitInput) => {
      const { comment } = await createReviewComment({
        description: input.description,
        rel_x: input.relX,
        rel_y: input.relY,
        page_num: input.page,
      });
      setPending({
        id: comment._id,
        askPuntt: offerSend && sendChecked ? input.description : null,
      });
      setLeftPane("comments");
    },
    [createReviewComment, offerSend, sendChecked, setLeftPane],
  );

  // why: the saved comment reaches `review.comments` one render after the
  // mutation resolves; selecting it synchronously misses the focus lookup,
  // and its chip needs the comment for its number and title.
  useEffect(() => {
    if (pending == null) return;
    if (!comments?.some((c) => c._id === pending.id)) return;
    selectComment(pending.id);
    setPending(null);
    if (pending.askPuntt == null) return;
    const context = commentContextFor(pending.id);
    if (!context) return;
    const message = pending.askPuntt;
    transport.setCommentContext(context);
    void transport.send({ message }).then((sent) => {
      // why: send refuses while a turn runs; the chip comes off so her next
      // message does not carry it, and the page's transport holds this one
      // until the turn ends (as the judge's rule request does).
      if (!sent) {
        transport.removeCommentContext(context.id);
        transport.sendWhenIdle({ message, commentContext: context });
      }
    });
  }, [pending, comments, selectComment, commentContextFor, transport]);

  const sendToPuntt = useMemo(
    (): ComposeSendToPuntt | null =>
      offerSend ? { checked: sendChecked, onChange: setSendChecked } : null,
    [offerSend, sendChecked],
  );

  return { handleSubmitHumanComment, sendToPuntt };
}
